import React from "react";
import { getVersionTorneoPublic } from "services/publicTorneosService";
import { enMinutos } from "components/torneos/horarios";

// Mantiene al día la pantalla pública de resultados sin recargar la página.
//
// En vez de volver a bajar el torneo cada tanto (son cientos de KB, con la lista
// de fe de todos los equipos), consulta un endpoint que devuelve solo la fecha de
// última modificación y recién entonces pide el torneo entero. Un chequeo pesa
// unas decenas de bytes.
//
// Uso:
//   const { refrescarAhora, refrescando } = useRefrescoResultados({
//     torneoId, activo, date, versionInicial, onRefrescar,
//   });

const SEGUNDO = 1000;
const MINUTO = 60 * SEGUNDO;

// Cadencia según lo que el usuario esté mirando: seguir una fecha que se está
// jugando no cuesta lo mismo que mirar los resultados de hace tres semanas.
const INTERVALO_EN_JUEGO = 15 * SEGUNDO;
const INTERVALO_HOY = 60 * SEGUNDO;
const INTERVALO_OTRO_DIA = 5 * MINUTO;

// Margen alrededor de los horarios cargados: los resultados se empiezan a cargar
// con los partidos en curso y siguen entrando un rato después del último.
const ANTES_DEL_PRIMERO = 30; // minutos
const DESPUES_DEL_ULTIMO = 180; // minutos

// Nadie sigue mirando una pantalla que dejó abierta hace veinte minutos.
const INACTIVIDAD = 20 * MINUTO;

// Si el servidor no contesta varias veces seguidas dejamos de insistir hasta que
// el usuario vuelva a dar señales de vida.
const FALLOS_PARA_PAUSAR = 3;

const EVENTOS_DE_ACTIVIDAD = ["click", "touchstart", "keydown", "scroll"];

// "YYYY-MM-DD" de hoy en hora local, que es como se guardan las fechas.
const hoyLocal = () => {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return d.getFullYear() + "-" + mes + "-" + dia;
};

// ¿El reloj está dentro de la ventana de juego de la fecha? Una fecha de hoy sin
// horarios cargados cuenta como en juego: no hay con qué descartarla.
const enVentanaDeJuego = (date) => {
  const horarios = (date.matches || []).map((m) => enMinutos(m.hora)).filter((v) => v != null);
  if (horarios.length === 0) return true;
  const ahora = new Date();
  const minutosDeAhora = ahora.getHours() * 60 + ahora.getMinutes();
  return (
    minutosDeAhora >= Math.min(...horarios) - ANTES_DEL_PRIMERO &&
    minutosDeAhora <= Math.max(...horarios) + DESPUES_DEL_ULTIMO
  );
};

export function intervaloPara(date) {
  if (!date || !date.date) return INTERVALO_OTRO_DIA;
  if (date.date !== hoyLocal()) return INTERVALO_OTRO_DIA;
  return enVentanaDeJuego(date) ? INTERVALO_EN_JUEGO : INTERVALO_HOY;
}

export function useRefrescoResultados({ torneoId, activo, date, versionInicial, onRefrescar }) {
  const [refrescando, setRefrescando] = React.useState(false);

  // Todo lo que cambia en cada render va por ref: el ciclo no se rearma salvo
  // que cambie el torneo o se salga de la pantalla de resultados.
  const version = React.useRef(versionInicial || null);
  const datosRef = React.useRef({ date, onRefrescar });
  datosRef.current = { date, onRefrescar };
  const enCurso = React.useRef(false);
  const fallos = React.useRef(0);
  const ultimaActividad = React.useRef(Date.now());

  React.useEffect(() => {
    version.current = versionInicial || null;
  }, [torneoId, versionInicial]);

  // Trae el torneo y anota qué versión quedó cargada.
  const refrescar = React.useCallback(async (versionNueva) => {
    if (enCurso.current) return;
    enCurso.current = true;
    setRefrescando(true);
    try {
      const confirmada = await datosRef.current.onRefrescar();
      version.current = confirmada || versionNueva || version.current;
    } finally {
      enCurso.current = false;
      setRefrescando(false);
    }
  }, []);

  // El botón de la pantalla: trae el torneo sin preguntar por la versión.
  const refrescarAhora = React.useCallback(async () => {
    ultimaActividad.current = Date.now();
    fallos.current = 0;
    const actual = await getVersionTorneoPublic(torneoId);
    await refrescar(actual);
  }, [torneoId, refrescar]);

  React.useEffect(() => {
    if (!activo || !torneoId) return undefined;

    let cancelado = false;
    let timer = null;

    const registrarActividad = () => {
      ultimaActividad.current = Date.now();
      fallos.current = 0;
    };

    const chequear = async () => {
      if (cancelado || enCurso.current) return;
      if (document.visibilityState !== "visible") return;
      if (Date.now() - ultimaActividad.current > INACTIVIDAD) return;
      if (fallos.current >= FALLOS_PARA_PAUSAR) return;

      const actual = await getVersionTorneoPublic(torneoId);
      if (cancelado) return;
      if (!actual) {
        fallos.current += 1;
        return;
      }
      fallos.current = 0;
      // El primer chequeo solo sincroniza si todavía no sabemos con qué versión
      // se cargó la pantalla.
      if (!version.current) {
        version.current = actual;
        return;
      }
      if (new Date(actual).getTime() !== new Date(version.current).getTime()) {
        await refrescar(actual);
      }
    };

    // setTimeout encadenado en vez de setInterval: la cadencia se recalcula en
    // cada vuelta, así que sigue el paso de la fecha que se está mirando.
    const programar = () => {
      if (cancelado) return;
      timer = setTimeout(async () => {
        await chequear();
        programar();
      }, intervaloPara(datosRef.current.date));
    };

    // Volver a la app es el momento en que más se nota el dato viejo.
    const alCambiarVisibilidad = () => {
      if (document.visibilityState !== "visible") return;
      registrarActividad();
      chequear();
    };

    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    EVENTOS_DE_ACTIVIDAD.forEach((evento) =>
      window.addEventListener(evento, registrarActividad, { passive: true })
    );
    programar();

    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
      EVENTOS_DE_ACTIVIDAD.forEach((evento) => window.removeEventListener(evento, registrarActividad));
    };
  }, [activo, torneoId, refrescar]);

  return { refrescarAhora, refrescando };
}

export default useRefrescoResultados;
